import { apiRoutes } from '@/constants/api-routes';
import { apiRequest } from './http';
import type { LoggedBookLookupResult, LoggedBookView } from '@/types/api';

/**
 * Payload for `POST /logged-books`. Only `title` is required. `coverUrl` is
 * either a managed `/covers/` URL from `uploadCover` (manual) or a provider
 * cover URL from `lookup` (ISBN) — the server validates the host and only
 * downloads from its allowlist, so passing the lookup value is safe.
 */
export interface CreateLoggedBookPayload {
  readerId: string;
  title: string;
  author?: string | null;
  isbn?: string | null;
  pageCount?: number | null;
  coverUrl?: string | null;
  rating?: number | null;
  note?: string | null;
  finishedOn?: string | null;
}

/**
 * Payload for `PATCH /logged-books/:id`. Omit a field to leave it unchanged;
 * pass `null` on a nullable field to clear it.
 */
export type UpdateLoggedBookPayload = Partial<Omit<CreateLoggedBookPayload, 'readerId'>>;

export const loggedBooksService = {
  /** A reader's Reading Log, newest first. */
  list(readerId: string) {
    return apiRequest<LoggedBookView[]>(apiRoutes.loggedBooks.list, {
      query: { readerId },
    });
  },

  /** Resolve title/author/cover from an ISBN. Rejects (404) when not found. */
  lookup(isbn: string) {
    return apiRequest<LoggedBookLookupResult>(apiRoutes.loggedBooks.lookup, {
      query: { isbn },
    });
  },

  create(payload: CreateLoggedBookPayload) {
    return apiRequest<LoggedBookView>(apiRoutes.loggedBooks.create, {
      method: 'POST',
      body: payload,
    });
  },

  update(id: string, payload: UpdateLoggedBookPayload) {
    return apiRequest<LoggedBookView>(apiRoutes.loggedBooks.one(id), {
      method: 'PATCH',
      body: payload,
    });
  },

  remove(id: string) {
    return apiRequest<void>(apiRoutes.loggedBooks.one(id), { method: 'DELETE' });
  },

  /**
   * Upload a cover image manually. Returns the managed URL to pass back on a
   * create/update body. Mirrors `booksService.uploadCover`'s multipart shape.
   */
  uploadCover(file: File) {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return apiRequest<{ coverUrl: string }>(apiRoutes.loggedBooks.cover, {
      method: 'POST',
      body: formData,
      raw: true, // let fetch set the multipart boundary header
    });
  },
};
